import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { RoadmapProject } from '@/services/roadmapMarketplace';

interface VisualRoadmapTimelineProps {
  projects: RoadmapProject[];
  roadmapName: string;
  totalDuration: number;
  onTimelineUpdate?: (projectId: string, updates: { estimated_duration_months?: number; start_month?: number; parent_id?: string | null; strategy_title?: string; strategy_description?: string }) => Promise<void>;
  isEditable?: boolean;
  onProjectClick?: (project: RoadmapProject) => void;
}

// ProjectNode interface for hierarchy tree
interface ProjectNode extends RoadmapProject {
  children?: ProjectNode[];
  level?: number;
  calculatedStart?: number;
  calculatedDuration?: number;
  // Custom properties for display purposes
  strategy_title?: string;
  strategy_description?: string;
  program_title?: string;
  program_description?: string;
}

// Dynamic initiative color generation - no hardcoded names
/** Status-based colors for planning draft (existing vs new vs changed). New = blue per user request. */
const DRAFT_STATUS_COLORS: Record<string, { primary: string; secondary: string; border: string }> = {
  existing: { primary: '#6B7280', secondary: '#E5E7EB', border: '#4B5563' },
  new: { primary: '#2563EB', secondary: '#DBEAFE', border: '#1D4ED8' },
  changed: { primary: '#D97706', secondary: '#FEF3C7', border: '#B45309' },
};

const generateInitiativeColor = (index: number) => {
  // Generate colors dynamically based on index
  const colors = [
    { primary: '#3B82F6', secondary: '#DBEAFE', border: '#1E40AF' }, // Blue
    { primary: '#10B981', secondary: '#D1FAE5', border: '#047857' }, // Green
    { primary: '#F59E0B', secondary: '#FEF3C7', border: '#D97706' }, // Amber
    { primary: '#8B5CF6', secondary: '#EDE9FE', border: '#7C3AED' }, // Purple
    { primary: '#EF4444', secondary: '#FEE2E2', border: '#DC2626' }, // Red
    { primary: '#06B6D4', secondary: '#CFFAFE', border: '#0891B2' }, // Cyan
    { primary: '#84CC16', secondary: '#ECFCCB', border: '#65A30D' }, // Lime
    { primary: '#F97316', secondary: '#FED7AA', border: '#EA580C' }, // Orange
    { primary: '#EC4899', secondary: '#FCE7F3', border: '#BE185D' }, // Pink
    { primary: '#6366F1', secondary: '#EEF2FF', border: '#4338CA' }, // Indigo
  ];
  
  return colors[index % colors.length];
};

// Optimized Project Component with Direct Mouse Events
const DraggableProject: React.FC<{
  project: RoadmapProject & { strategy_title?: string; strategy_description?: string; program_title?: string; program_description?: string } & Partial<ProjectNode>;
  initiativeColor: any;
  totalDuration: number;
  isEditable: boolean;
  onProjectClick?: (project: RoadmapProject) => void;
  onTimelineUpdate?: (projectId: string, updates: { estimated_duration_months?: number; start_month?: number; parent_id?: string | null; strategy_title?: string; strategy_description?: string }) => Promise<void>;
  isDragging?: boolean;
  dragOffset?: number;
  hasDragged?: boolean;
  dragType?: 'move' | 'resize-start' | 'resize-end' | 'nest' | null;
  dragStartX?: number;
  dragCurrentX?: number;
  onMouseDown?: (e: React.MouseEvent, project: RoadmapProject, dragType: 'move' | 'resize-start' | 'resize-end') => void;
  hierarchyLevel?: number;
  isDropTarget?: boolean;
}> = ({ project, initiativeColor, totalDuration, isEditable, onProjectClick, isDragging, dragOffset = 0, hasDragged = false, dragType = null, dragStartX = 0, dragCurrentX = 0, onMouseDown, hierarchyLevel = 0, isDropTarget = false }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipAnchor, setTooltipAnchor] = React.useState<DOMRect | null>(null);
  const barRef = React.useRef<HTMLDivElement>(null);
  const handleMouseEnter = React.useCallback(() => {
    setShowTooltip(true);
    const el = barRef.current;
    if (el) setTooltipAnchor(el.getBoundingClientRect());
  }, []);
  const handleMouseLeave = React.useCallback(() => {
    setShowTooltip(false);
    setTooltipAnchor(null);
  }, []);
  // Calculate visual position and size from project
  // The project comes from localProjects which is already updated with dragged position
  // Use calculated span for parent projects, otherwise use actual values
  const projectNode = project as ProjectNode;
  const projectStartMonth = projectNode.calculatedStart !== undefined 
    ? projectNode.calculatedStart 
    : (project.start_month || 0);
  const projectDuration = projectNode.calculatedDuration !== undefined
    ? projectNode.calculatedDuration
    : project.estimated_duration_months;
  
  let visualStartPercent = (projectStartMonth / totalDuration) * 100;
  let visualWidthPercent = (projectDuration / totalDuration) * 100;
  
  // For MOVE operations during drag, use transform to show real-time feedback
  // For resize operations, calculate new position based on drag delta
  if (isDragging && dragType) {
    const timelineRect = document.querySelector('.timeline-container')?.getBoundingClientRect();
    if (timelineRect) {
      const mouseDeltaX = dragCurrentX - dragStartX;
      const mouseDeltaPercentage = mouseDeltaX / timelineRect.width;
      const monthDelta = mouseDeltaPercentage * totalDuration;
      const roundedMonthDelta = Math.round(monthDelta);
      
      if (dragType === 'move') {
        // During move, the transform will handle visual position
        // Base position stays the same, transform shows the drag offset
      } else if (dragType === 'resize-start') {
        // Resize from start: change both start position and width
        const newStartMonth = Math.max(0, projectStartMonth + roundedMonthDelta);
        const newDuration = Math.max(1, projectDuration - roundedMonthDelta);
        visualStartPercent = (newStartMonth / totalDuration) * 100;
        visualWidthPercent = (newDuration / totalDuration) * 100;
      } else if (dragType === 'resize-end') {
        // Resize from end: change only width
        const newDuration = Math.max(1, projectDuration + roundedMonthDelta);
        visualWidthPercent = (newDuration / totalDuration) * 100;
      }
    }
  }

  // Calculate indentation for hierarchy visualization
  const indentPixels = hierarchyLevel * 20;
  
  return (
    <div
      ref={barRef}
      data-project-id={project.id}
      style={{
        left: `${visualStartPercent}%`,
        width: `${visualWidthPercent}%`,
        minWidth: '60px',
        marginLeft: `${indentPixels}px`,
        transform: isDragging && dragType === 'move' ? `translateX(${dragOffset}px)` : 'none',
        zIndex: isDragging ? 20 : showTooltip ? 10 : 1
      }}
      className={`absolute top-0 group ${isDropTarget ? 'ring-4 ring-green-400 ring-opacity-75' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Project name above bar (top-aligned) */}
      <div className="flex flex-col items-stretch w-full">
        <h5 className="font-medium text-xs text-[var(--color-surface-text)] break-words leading-tight mb-0.5 w-full text-left line-clamp-2">
          {project.name}
        </h5>
        {/* Project Bar */}
        <div
          className={`h-4 w-full mx-0.5 rounded border transition-all duration-200 relative flex items-center justify-center flex-shrink-0 ${
            isEditable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
          } ${isDragging ? 'ring-2 ring-blue-500 shadow-2xl scale-110 bg-blue-100' : ''}`}
          style={{
            backgroundColor: initiativeColor.secondary,
            borderColor: initiativeColor.border,
            minWidth: '60px'
          }}
          onMouseDown={(e) => {
            if (isEditable && onMouseDown) {
              onMouseDown(e, project, 'move');
            }
          }}
          onClick={(e) => {
            if (!isDragging && !hasDragged && onProjectClick) {
              e.stopPropagation();
              onProjectClick(project);
            }
          }}
        >

        {/* Drag Indicator */}
        {isEditable && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="text-white text-xs font-bold">⋮⋮</span>
          </div>
        )}

        {/* Resize Handles */}
        {isEditable && (
          <>
            {/* Start Resize Handle */}
            <div
              className="absolute left-0 top-0 w-2 h-full bg-gray-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-ew-resize"
                       onMouseDown={(e) => {
                         e.stopPropagation();
                         if (onMouseDown) {
                           onMouseDown(e, project, 'resize-start');
                         }
                       }}
            />
            
            {/* End Resize Handle */}
            <div
              className="absolute right-0 top-0 w-2 h-full bg-gray-400 opacity-0 group-hover:opacity-100 transition-opacity cursor-ew-resize"
                       onMouseDown={(e) => {
                         e.stopPropagation();
                         if (onMouseDown) {
                           onMouseDown(e, project, 'resize-end');
                         }
                       }}
            />
          </>
        )}

        {/* Status Indicator */}
        <div
          className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full border border-white ${
            project.status === 'ACTIVE' ? 'bg-green-500' :
            project.status === 'COMPLETED' ? 'bg-blue-500' :
            project.status === 'ON_HOLD' ? 'bg-yellow-500' :
            'bg-gray-400'
          }`}
        ></div>
        </div>
      </div>

      {/* Hover Tooltip: rendered in portal so it is not clipped by right panel overflow */}
      {showTooltip && !isDragging && tooltipAnchor && typeof document !== 'undefined' && (() => {
        const draft = (project as { _draftProject?: Record<string, unknown> })._draftProject;
        const cx = tooltipAnchor.left + tooltipAnchor.width / 2;
        const top = tooltipAnchor.top;
        const showAbove = top > 140;
        const tooltipStyle: React.CSSProperties = {
          position: 'fixed',
          left: cx,
          top: showAbove ? top : tooltipAnchor.bottom,
          transform: showAbove ? 'translate(-50%, -100%) translateY(-8px)' : 'translate(-50%, 8px)',
          zIndex: 9999,
          maxHeight: '80vh',
          overflow: 'auto',
        };
        let content: React.ReactNode;
        if (draft) {
          const desc = draft['description'] != null ? String(draft['description']).trim() : null;
          const benefitsRaw = draft['benefits'];
          const benefits = benefitsRaw == null ? [] : Array.isArray(benefitsRaw)
            ? benefitsRaw.map((b: unknown) => (b != null ? String(b).trim() : '')).filter(Boolean)
            : typeof benefitsRaw === 'string' ? (benefitsRaw.trim() ? [benefitsRaw.trim()] : []) : [];
          const stakeholdersRaw = Array.isArray(draft['stakeholders']) ? draft['stakeholders'] : [];
          const stakeholders = stakeholdersRaw
            .map((s: unknown) => (s != null && typeof s === 'object' ? (String((s as Record<string, unknown>)['position'] ?? (s as Record<string, unknown>)['role'] ?? (s as Record<string, unknown>)['name'] ?? '').trim()) : ''))
            .filter(Boolean);
          const risks = Array.isArray(draft['risks'])
            ? draft['risks']
                .map((r: unknown) => {
                  if (r == null) return '';
                  if (typeof r === 'string') return r.trim();
                  if (typeof r === 'object') {
                    const o = r as Record<string, unknown>;
                    const text = (o['description'] ?? o['title'] ?? o['name'] ?? o['risk'] ?? o['text']) as string | undefined;
                    return text != null ? String(text).trim() : '';
                  }
                  return String(r).trim();
                })
                .filter(Boolean)
            : [];
          const cost = draft['cost'] != null ? String(draft['cost']) : null;
          const startDateVal = draft['start_date'] ?? draft['startDate'];
          const endDateVal = draft['end_date'] ?? draft['endDate'];
          const startDate = startDateVal != null ? String(startDateVal) : null;
          const endDate = endDateVal != null ? String(endDateVal) : null;
          content = (
            <div className="w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none">
              <div className="font-semibold mb-2 text-sm">{project.name}</div>
              {desc && <div className="mb-2 text-gray-300"><span className="text-gray-400">Description:</span> {desc}</div>}
              {benefits.length > 0 && (
                <div className="mb-2"><span className="text-gray-400">Benefits:</span><ul className="list-disc list-inside mt-0.5">{benefits.map((b, i) => <li key={i}>{b}</li>)}</ul></div>
              )}
              {stakeholders.length > 0 && <div className="mb-2"><span className="text-gray-400">Stakeholders:</span> {stakeholders.join('; ')}</div>}
              {risks.length > 0 && (
                <div className="mb-2"><span className="text-gray-400">Risks:</span><ul className="list-disc list-inside mt-0.5">{risks.map((r, i) => <li key={i}>{r}</li>)}</ul></div>
              )}
              {(cost || startDate || endDate) && (
                <div className="space-y-0.5 text-gray-400">
                  {cost && <div>Cost: {String(cost)}</div>}
                  {(startDate || endDate) && <div>Start: {String(startDate ?? '—')} · End: {String(endDate ?? '—')}</div>}
                </div>
              )}
            </div>
          );
        } else {
          content = (
            <div className="w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none">
              <div className="font-semibold mb-2 text-sm">{project.name}</div>
              {project.description && (
                <div className="mb-2 text-gray-300">{project.description}</div>
              )}
              <div className="space-y-1">
                {project.hierarchy_type === 'PROGRAM' && (project as any).program_title && (
                  <div><span className="text-gray-400">Program:</span> {(project as any).program_title}</div>
                )}
                {(project as any).strategy_title && (
                  <div><span className="text-gray-400">Strategy:</span> {(project as any).strategy_title}</div>
                )}
                <div><span className="text-gray-400">Type:</span> {project.hierarchy_type}</div>
                <div><span className="text-gray-400">Duration:</span> {project.estimated_duration_months} months</div>
                <div><span className="text-gray-400">Start:</span> Month {project.start_month || 0}</div>
                {(project.estimated_cost_range_min || project.estimated_cost_range_max) && (
                  <div>
                    <span className="text-gray-400">Cost:</span> $
                    {project.estimated_cost_range_min && project.estimated_cost_range_max
                      ? `${project.estimated_cost_range_min}K - ${project.estimated_cost_range_max}K`
                      : project.estimated_cost_range_min
                      ? `${project.estimated_cost_range_min}K`
                      : `${project.estimated_cost_range_max}K`}
                  </div>
                )}
                <div><span className="text-gray-400">Status:</span> {project.status}</div>
                <div><span className="text-gray-400">Complexity:</span> {project.complexity_level}/5</div>
              </div>
            </div>
          );
        }
        return ReactDOM.createPortal(<div style={tooltipStyle}>{content}</div>, document.body);
      })()}

    </div>
  );
};

// Get strategy name for grouping projects
const getStrategyForProject = (project: RoadmapProject & { strategy_title?: string; strategy_description?: string }): string => {
  // Use strategy_title if available
  if (project.strategy_title) {
    return project.strategy_title;
  }
  
  // Fallback - shouldn't happen with proper data
  return 'Unlinked Projects';
};



export const VisualRoadmapTimeline: React.FC<VisualRoadmapTimelineProps> = ({
  projects,
  roadmapName: _roadmapName,
  totalDuration: _propTotalDuration,
  onTimelineUpdate,
  isEditable = false,
  onProjectClick
}) => {
  const { t } = useLanguage();

  // Simple drag state
  const [draggingProject, setDraggingProject] = useState<string | null>(null);
  const [dragStartX, setDragStartX] = useState<number>(0);
  const [dragCurrentX, setDragCurrentX] = useState<number>(0);
  const [hasDragged, setHasDragged] = useState(false);
  const [dragType, setDragType] = useState<'move' | 'resize-start' | 'resize-end' | 'nest' | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null); // Project ID that can accept the drop
  
  // Use refs to avoid stale closure issues in callbacks
  const dragStartXRef = React.useRef<number>(0);
  const dragTypeRef = React.useRef<'move' | 'resize-start' | 'resize-end' | 'nest' | null>(null);
  const draggingProjectRef = React.useRef<string | null>(null);
  
  // Track which projects have pending updates to prevent useEffect from resetting them
  const [pendingUpdates, setPendingUpdates] = useState<Set<string>>(new Set());
  
  // Track when projects were last dragged to preserve their positions even after pendingUpdates clears
  // This prevents snap-back from stale props that arrive after the backend update completes
  const lastDragTimeRef = React.useRef<Map<string, number>>(new Map());
  
  // Local projects state - when user drags, we immediately update this to reflect the new position
  // This allows swimlane recalculation with the new position and keeps things where user dropped them
  const [localProjects, setLocalProjects] = useState<RoadmapProject[]>(projects);
  
  // Simple total duration calculation - use localProjects so it accounts for dragged positions
  const totalDuration = Math.max(36, ...localProjects.map(p => (p.start_month || 0) + p.estimated_duration_months));
  
  // Sync local projects with props when props change
  // But DON'T sync projects that have pending updates (being dragged or just updated)
  // Also preserve local projects that have different positions (to prevent snap-back)
  React.useEffect(() => {
    if (draggingProject) {
      // Don't sync while actively dragging
      return;
    }
    
    // Merge props with localProjects, preserving any pending updates or position differences
    setLocalProjects(prevProjects => {
      const merged = projects.map(propProject => {
        // Find the local version of this project
        const localProject = prevProjects.find(p => p.id === propProject.id);
        
        // If this project has a pending update, ALWAYS keep the local version
        if (pendingUpdates.has(propProject.id)) {
          return localProject || propProject;
        }
        
        // If we have a local version with a different position, preserve it
        // This prevents snap-back when parent reloads with stale data
        if (localProject) {
          const localStartMonth = localProject.start_month || 0;
          const propStartMonth = propProject.start_month || 0;
          const localDuration = localProject.estimated_duration_months;
          const propDuration = propProject.estimated_duration_months;
          
          // Check if this project was recently dragged (within last 10 seconds)
          const lastDragTime = lastDragTimeRef.current.get(propProject.id);
          const recentlyDragged = lastDragTime && (Date.now() - lastDragTime) < 10000; // 10 seconds
          
          // If position or duration differs, keep local version (user just dragged it)
          // OR if project was recently dragged, always preserve local position (even if positions match)
          // This prevents snap-back from stale props that arrive after backend update
          if (localStartMonth !== propStartMonth || localDuration !== propDuration || recentlyDragged) {
            // If positions match AND it was recently dragged, we can clear the pending update
            // This means the backend update was successful and props are now in sync
            if (localStartMonth === propStartMonth && localDuration === propDuration && recentlyDragged) {
              // Props now match local state - backend update was successful
              // Clear pending update and drag time tracking
              setPendingUpdates(prev => {
                const next = new Set(prev);
                next.delete(propProject.id);
                return next;
              });
              lastDragTimeRef.current.delete(propProject.id);
            }
            return localProject;
          }
        }
        
        // Otherwise use the prop version
        return propProject;
      });
      
      return merged;
    });
  }, [projects, draggingProject, pendingUpdates]);
  
  const timelineRef = useRef<HTMLDivElement>(null);

  // Build hierarchy tree from projects using parent_id
  const buildHierarchyTree = (projects: RoadmapProject[]): ProjectNode[] => {
    const projectMap = new Map<string, ProjectNode>();
    const rootProjects: ProjectNode[] = [];

    // First pass: create all nodes
    projects.forEach(project => {
      projectMap.set(project.id, { ...project, children: [], level: 0 });
    });

    // Second pass: build tree and calculate parent spans
    const calculateParentSpan = (node: ProjectNode): { start: number; end: number } => {
      const nodeStart = node.start_month || 0;
      const nodeEnd = nodeStart + node.estimated_duration_months;
      
      if (node.children && node.children.length > 0) {
        // Calculate span from all children
        let minStart = nodeStart;
        let maxEnd = nodeEnd;
        
        node.children.forEach(child => {
          const childSpan = calculateParentSpan(child);
          minStart = Math.min(minStart, childSpan.start);
          maxEnd = Math.max(maxEnd, childSpan.end);
        });
        
        // Update parent's calculated span
        node.calculatedStart = minStart;
        node.calculatedDuration = maxEnd - minStart;
        
        return { start: minStart, end: maxEnd };
      }
      
      return { start: nodeStart, end: nodeEnd };
    };

    // Build parent-child relationships
    projects.forEach(project => {
      const node = projectMap.get(project.id)!;
      if (project.parent_id) {
        const parent = projectMap.get(project.parent_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(node);
          node.level = (parent.level || 0) + 1;
        } else {
          // Parent not found, treat as root
          rootProjects.push(node);
        }
      } else {
        // No parent, it's a root project
        rootProjects.push(node);
      }
    });

    // Calculate spans for all nodes (bottom-up)
    const calculateAllSpans = (nodes: ProjectNode[]) => {
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          calculateAllSpans(node.children);
          calculateParentSpan(node);
        }
      });
    };

    calculateAllSpans(rootProjects);

    return rootProjects;
  };

  // Group projects by strategy and build hierarchy within each strategy
  const strategies = React.useMemo(() => {
    const grouped = localProjects.reduce((acc, project) => {
      const strategyName = getStrategyForProject(project);
      if (!acc[strategyName]) {
        acc[strategyName] = [];
      }
      acc[strategyName].push(project);
      return acc;
    }, {} as Record<string, (RoadmapProject & { strategy_title?: string; strategy_description?: string; program_title?: string; program_description?: string })[]>);

    // Build hierarchy tree for each strategy
    const strategiesWithHierarchy: Record<string, ProjectNode[]> = {};
    Object.entries(grouped).forEach(([strategyName, strategyProjects]) => {
      strategiesWithHierarchy[strategyName] = buildHierarchyTree(strategyProjects);
    });

    return strategiesWithHierarchy;
  }, [localProjects]);

  // Flatten hierarchy tree for swimlane calculation while preserving hierarchy info
  const flattenHierarchy = (nodes: ProjectNode[]): ProjectNode[] => {
    const result: ProjectNode[] = [];
    const traverse = (node: ProjectNode) => {
      result.push(node);
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => traverse(child));
      }
    };
    nodes.forEach(node => traverse(node));
    return result;
  };

  // Calculate swimlanes dynamically, but preserve existing assignments when possible
  // IMPORTANT: Process projects in hierarchy order (depth-first) to ensure all children
  // of a folder are shown before moving to the next sibling
  const calculateSwimlanesWithPreservation = (hierarchyNodes: ProjectNode[], existingSwimlanes: RoadmapProject[][] = []) => {
    // Flatten hierarchy for swimlane calculation - this maintains depth-first order
    const initiativeProjects = flattenHierarchy(hierarchyNodes);
    const swimlanes: RoadmapProject[][] = [...existingSwimlanes]; // Start with existing swimlanes
  const placedProjects = new Set<string>();
  
    // Process projects in hierarchy order (depth-first) - don't sort by time
    // This ensures all children of a folder are shown before moving to the next sibling
    const projectsToProcess = [...initiativeProjects];
  
    for (const project of projectsToProcess) {
      if (placedProjects.has(project.id)) continue;
      // Use calculated span for parent projects, otherwise use actual start_month
      const projectNode = project as ProjectNode;
      let startMonth = projectNode.calculatedStart !== undefined 
        ? projectNode.calculatedStart 
        : (project.start_month || 0);
      const duration = projectNode.calculatedDuration !== undefined
        ? projectNode.calculatedDuration
        : project.estimated_duration_months;
      
      // Calculate start month based on dependencies (only if dependencies exist)
    if (project.dependencies && project.dependencies.length > 0) {
        const dependencyBasedStart = Math.max(...project.dependencies.map((depId: string) => {
        const depProject = initiativeProjects.find(p => p.id === depId);
        return depProject ? (depProject.start_month || 0) + depProject.estimated_duration_months : 0;
      }));
        
        startMonth = Math.max(startMonth, dependencyBasedStart);
      }
      
      // Try to find existing swimlane for this project first
      let targetSwimlaneIndex = -1;
      for (let i = 0; i < swimlanes.length; i++) {
        if (swimlanes[i].some(p => p.id === project.id)) {
          targetSwimlaneIndex = i;
          break;
        }
      }
      
      // Check if the project can stay in its current swimlane
      if (targetSwimlaneIndex >= 0) {
        const currentSwimlane = swimlanes[targetSwimlaneIndex];
        let hasConflict = false;
        
        for (const existingProject of currentSwimlane) {
          if (existingProject.id === project.id) continue; // Skip self
          
          const existingNode = existingProject as ProjectNode;
          const existingStart = existingNode.calculatedStart !== undefined 
            ? existingNode.calculatedStart 
            : (existingProject.start_month || 0);
          const existingDuration = existingNode.calculatedDuration !== undefined
            ? existingNode.calculatedDuration
            : existingProject.estimated_duration_months;
          const existingEnd = existingStart + existingDuration;
          const newEnd = startMonth + duration;
          
          // Check for actual overlap
          if (!(startMonth >= existingEnd || existingStart >= newEnd)) {
            hasConflict = true;
            break;
          }
        }
        
        if (!hasConflict) {
          // Project can stay in current swimlane
          project.start_month = startMonth;
          placedProjects.add(project.id);
          continue;
        }
      }
      
      // Project needs to move - first remove it from its old swimlane
      if (targetSwimlaneIndex >= 0) {
        swimlanes[targetSwimlaneIndex] = swimlanes[targetSwimlaneIndex].filter(p => p.id !== project.id);
      }
      
      // Find first available swimlane
    let swimlaneIndex = 0;
    while (swimlaneIndex < swimlanes.length) {
      const swimlane = swimlanes[swimlaneIndex];
        let hasConflict = false;
        
        for (const existingProject of swimlane) {
          const existingNode = existingProject as ProjectNode;
          const existingStart = existingNode.calculatedStart !== undefined 
            ? existingNode.calculatedStart 
            : (existingProject.start_month || 0);
          const existingDuration = existingNode.calculatedDuration !== undefined
            ? existingNode.calculatedDuration
            : existingProject.estimated_duration_months;
          const existingEnd = existingStart + existingDuration;
        const newEnd = startMonth + duration;
          
          // Check for actual overlap
          if (!(startMonth >= existingEnd || existingStart >= newEnd)) {
            hasConflict = true;
            break;
          }
        }
      
      if (!hasConflict) break;
      swimlaneIndex++;
    }
    
    // Create new swimlane if needed
    if (swimlaneIndex >= swimlanes.length) {
      swimlanes.push([]);
    }
    
    // Place project (update calculated values if it's a parent)
    // projectNode is already declared earlier in the function
    if (projectNode.calculatedStart !== undefined) {
      projectNode.calculatedStart = startMonth;
    } else {
      project.start_month = startMonth;
    }
    if (projectNode.calculatedDuration !== undefined) {
      projectNode.calculatedDuration = duration;
    }
    swimlanes[swimlaneIndex].push(project);
    placedProjects.add(project.id);
    }
  
  return swimlanes;
};

  // Store swimlanes and only recalculate when necessary
  const [swimlanes, setSwimlanes] = React.useState<Record<string, RoadmapProject[][]>>({});

  // Calculate swimlanes when projects change (memoized to prevent unnecessary recalculations)
  React.useEffect(() => {
    const newSwimlanes: Record<string, RoadmapProject[][]> = {};
    Object.entries(strategies).forEach(([strategyName, strategyHierarchy]) => {
      newSwimlanes[strategyName] = calculateSwimlanesWithPreservation(strategyHierarchy, swimlanes[strategyName]);
    });
    setSwimlanes(newSwimlanes);
  }, [projects, strategies]); // Only recalculate when projects or strategies actually change

  // Simple mouse event handlers
  const handleMouseDown = (e: React.MouseEvent, project: RoadmapProject, dragType: 'move' | 'resize-start' | 'resize-end' = 'move') => {
    if (!isEditable || !onTimelineUpdate) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Update both state and refs
    setDraggingProject(project.id);
    setDragStartX(e.clientX);
    setDragCurrentX(e.clientX);
    setHasDragged(false);
    setDragType(dragType);
    
    // Update refs for use in callbacks
    dragStartXRef.current = e.clientX;
    dragTypeRef.current = dragType;
    draggingProjectRef.current = project.id;
  };

  // Helper function to check if a project is a descendant of another project
  const isDescendantOf = useCallback((childId: string, ancestorId: string, projects: RoadmapProject[]): boolean => {
    // A project cannot be a descendant of itself
    if (childId === ancestorId) {
      return false;
    }
    
    const childProject = projects.find(p => p.id === childId);
    if (!childProject) {
      return false;
    }
    
    // Walk up the parent chain to see if we reach the ancestor
    let currentParentId = childProject.parent_id;
    const visited = new Set<string>(); // Prevent infinite loops
    
    while (currentParentId) {
      // Prevent infinite loops in case of circular references
      if (visited.has(currentParentId)) {
        break;
      }
      visited.add(currentParentId);
      
      // Found the ancestor in the parent chain
      if (currentParentId === ancestorId) {
        return true;
      }
      
      // Move up to the next parent
      const parent = projects.find(p => p.id === currentParentId);
      currentParentId = parent?.parent_id || undefined;
    }
    
    return false;
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingProjectRef.current) return;
    
    // Always update drag position for real-time visual feedback
    setDragCurrentX(e.clientX);
    
    // Mark as dragged if mouse moved more than 5 pixels
    if (Math.abs(e.clientX - dragStartXRef.current) > 5) {
      setHasDragged(true);
    }
    
    // Check for drop targets (other projects we can nest into)
    // Only check if we're in 'move' mode (not resize)
    if (dragTypeRef.current === 'move' && timelineRef.current) {
      const elementUnderCursor = document.elementFromPoint(e.clientX, e.clientY);
      if (elementUnderCursor) {
        // Find the project element (look for data-project-id attribute)
        const projectElement = elementUnderCursor.closest('[data-project-id]');
        if (projectElement) {
          const targetProjectId = projectElement.getAttribute('data-project-id');
          const draggingId = draggingProjectRef.current;
          
          // Don't allow dropping on self
          if (targetProjectId && targetProjectId !== draggingId) {
            // CRITICAL: Check if target is a descendant of dragging project
            // If so, nesting would create a circular dependency and orphan both projects
            const targetIsDescendant = isDescendantOf(targetProjectId, draggingId, localProjects);
            
            if (!targetIsDescendant) {
              // Safe to nest - target is not a child/grandchild/etc. of dragging project
              setDropTarget(targetProjectId);
              return;
            }
            // If target IS a descendant, don't set it as drop target (prevent nesting)
          }
        }
      }
    }
    
    // Clear drop target if not over a valid target
    setDropTarget(null);
  }, [localProjects, isDescendantOf]);

  const handleMouseUp = useCallback(async (e: MouseEvent) => {
    // Always clear drag state, even if something goes wrong
    const currentDraggingProject = draggingProjectRef.current;
    const currentDragStartX = dragStartXRef.current;
    const currentDragType = dragTypeRef.current;
    
    // Clear refs immediately to prevent re-triggering
    draggingProjectRef.current = null;
    dragStartXRef.current = 0;
    dragTypeRef.current = null;
    
    // Clear state immediately
    setDraggingProject(null);
    setDragStartX(0);
    setDragCurrentX(0);
    setHasDragged(false);
    setDragType(null);
    setDropTarget(null);
    
    if (!currentDraggingProject || !onTimelineUpdate) {
      return;
    }
    
    const project = localProjects.find(p => p.id === currentDraggingProject);
    if (!project) {
      return;
    }
    
    if (!timelineRef.current) {
      return;
    }
    
    // Helper function to check if a project is a descendant of another project
    const isDescendantOf = (childId: string, ancestorId: string, projects: RoadmapProject[]): boolean => {
      // A project cannot be a descendant of itself
      if (childId === ancestorId) {
        return false;
      }
      
      const childProject = projects.find(p => p.id === childId);
      if (!childProject) {
        return false;
      }
      
      // Walk up the parent chain to see if we reach the ancestor
      let currentParentId = childProject.parent_id;
      const visited = new Set<string>(); // Prevent infinite loops
      
      while (currentParentId) {
        // Prevent infinite loops in case of circular references
        if (visited.has(currentParentId)) {
          break;
        }
        visited.add(currentParentId);
        
        // Found the ancestor in the parent chain
        if (currentParentId === ancestorId) {
          return true;
        }
        
        // Move up to the next parent
        const parent = projects.find(p => p.id === currentParentId);
        currentParentId = parent?.parent_id || undefined;
      }
      
      return false;
    };

    // Check if we're nesting (dropping on another project) or moving to a different strategy
    const elementUnderCursor = document.elementFromPoint(e.clientX, e.clientY);
    let newParentId: string | null | undefined = undefined;
    let newStrategyTitle: string | undefined = undefined;
    let newStrategyDescription: string | undefined = undefined;
    
    if (currentDragType === 'move' && elementUnderCursor) {
      // First, check if we're dropping on a strategy container (moving to a different strategy)
      const strategyContainer = elementUnderCursor.closest('[data-strategy-name]');
      if (strategyContainer) {
        const targetStrategyName = strategyContainer.getAttribute('data-strategy-name');
        if (targetStrategyName) {
          // Find the target strategy's description from the projects
          const targetStrategyProject = localProjects.find(p => (p as any).strategy_title === targetStrategyName);
          if (targetStrategyProject) {
            newStrategyTitle = targetStrategyName;
            newStrategyDescription = (targetStrategyProject as any).strategy_description;
            // When moving to a different strategy, remove parent_id (move to root of new strategy)
            newParentId = undefined;
          }
        }
      }
      
      // Check if we're dropping on another project (nesting)
      if (!newStrategyTitle) {
        const projectElement = elementUnderCursor.closest('[data-project-id]');
        if (projectElement) {
          const targetProjectId = projectElement.getAttribute('data-project-id');
          if (targetProjectId && targetProjectId !== currentDraggingProject) {
            // Find the target project to check its strategy
            const targetProject = localProjects.find(p => p.id === targetProjectId);
            
            // CRITICAL: Check if target is a descendant of dragging project
            // If so, nesting would create a circular dependency and orphan both projects
            const targetIsDescendant = isDescendantOf(targetProjectId, currentDraggingProject, localProjects);
            
            if (!targetIsDescendant && targetProject) {
              // Safe to nest - target is not a child/grandchild/etc. of dragging project
              newParentId = targetProjectId;
              
              // If target project is in a different strategy, update strategy too
              if ((targetProject as any).strategy_title && (targetProject as any).strategy_title !== (project as any).strategy_title) {
                newStrategyTitle = (targetProject as any).strategy_title;
                newStrategyDescription = (targetProject as any).strategy_description;
              }
              // When nesting in the same strategy, keep the same strategy (don't change strategy_title)
            }
            // If target IS a descendant, don't nest (prevent circular dependency)
          }
        }
      }
    }
    
    // Calculate the movement delta (difference between start and end mouse positions)
    const mouseDeltaX = e.clientX - currentDragStartX;
    
    // Convert mouse delta to timeline delta
    const timelineRect = timelineRef.current.getBoundingClientRect();
    const mouseDeltaPercentage = mouseDeltaX / timelineRect.width;
    const monthDelta = mouseDeltaPercentage * totalDuration;
    const roundedMonthDelta = Math.round(monthDelta);
    
    // Apply the delta based on drag type
    const originalStartMonth = project.start_month || 0;
    const originalDuration = project.estimated_duration_months;
    let newStartMonth = originalStartMonth;
    let newDuration = originalDuration;
    
    // Check if this is a program (has children)
    const projectNode = project as ProjectNode;
    const isProgram = (projectNode.children && projectNode.children.length > 0) || project.hierarchy_type === 'PROGRAM';
    
    // Always calculate newStartMonth for move operations, even when nesting
    // This ensures the position is updated even if we're also changing parent
    if (currentDragType === 'move') {
      if (newParentId === undefined) {
        // Move the entire project along timeline (not nesting)
        // For programs: Both start and end dates move together (duration stays same)
        // For regular projects: Both start and end dates move together (duration stays same)
        newStartMonth = Math.max(0, originalStartMonth + roundedMonthDelta);
        // Duration stays the same when moving - both start and end move together
        newDuration = originalDuration;
      } else {
        // Nesting: Still update start_month if user dragged it (position might have changed)
        // The position change is relative to the timeline, not the parent
        newStartMonth = Math.max(0, originalStartMonth + roundedMonthDelta);
        // Duration stays the same when nesting
        newDuration = originalDuration;
      }
      
      // If this is a program, we need to move all descendants too
      // The children will be updated in the localProjects update below
    } else if (currentDragType === 'resize-start') {
      // Resize from the start (change start month and duration)
      // For programs: Only change the program's span, don't move children
      // For regular projects: Change start month and duration normally
      
      // Validate: Check if there's space to resize left (newStartMonth >= 0)
      const proposedNewStartMonth = originalStartMonth + roundedMonthDelta;
      if (proposedNewStartMonth < 0) {
        // Not enough space to the left - don't allow resize
        return;
      }
      
      if (isProgram) {
        // For programs, resize-start only changes the program's start and duration
        // Children's start_month values stay the same (they don't move)
        newStartMonth = proposedNewStartMonth;
        newDuration = Math.max(1, originalDuration - roundedMonthDelta);
      } else {
        // For regular projects, resize-start works normally
        newStartMonth = proposedNewStartMonth;
        newDuration = Math.max(1, originalDuration - roundedMonthDelta);
      }
    } else if (currentDragType === 'resize-end') {
      // Resize from the end (change duration only)
      // For programs: Only change duration, children don't move
      // For regular projects: Change duration normally
      
      // Validate: Check if there's space to resize right
      const proposedNewDuration = originalDuration + roundedMonthDelta;
      if (proposedNewDuration < 1) {
        // Duration must be at least 1 month
        return;
      }
      
      // Validate: Check if there's space to resize right
      // The new end date should be reasonable (at least allow expansion up to 2x current timeline)
      const newEndMonth = originalStartMonth + proposedNewDuration;
      const maxAllowedEndMonth = Math.max(totalDuration * 1.5, 120); // Allow up to 1.5x timeline or 10 years, whichever is larger
      if (newEndMonth > maxAllowedEndMonth) {
        // Not enough space to the right - don't allow resize beyond reasonable limit
        return;
      }
      
      newDuration = proposedNewDuration;
    }
    
    // Check what changed
    const startMonthChanged = newStartMonth !== (project.start_month || 0);
    const durationChanged = newDuration !== project.estimated_duration_months;
    const parentChanged = newParentId !== undefined && newParentId !== (project.parent_id || null);
    const strategyChanged = newStrategyTitle !== undefined && newStrategyTitle !== (project as any).strategy_title;
    
    // Only update if something actually changed
    if (startMonthChanged || durationChanged || parentChanged || strategyChanged) {
      try {
        // Helper function to get all descendant project IDs
        const getAllDescendants = (projectId: string, allProjects: RoadmapProject[]): string[] => {
          const descendants: string[] = [];
          const children = allProjects.filter(p => p.parent_id === projectId);
          children.forEach(child => {
            descendants.push(child.id);
            descendants.push(...getAllDescendants(child.id, allProjects));
          });
          return descendants;
        };
        
        // Check if timeline needs to be expanded before updating
        
        const updates: { start_month?: number; estimated_duration_months?: number; parent_id?: string | null; strategy_title?: string; strategy_description?: string } = {};
        if (startMonthChanged) {
          updates.start_month = newStartMonth;
        } else if (currentDragType === 'move' && Math.abs(roundedMonthDelta) > 0) {
          // If we dragged but startMonth didn't change (maybe due to rounding), still update it
          // This prevents snap-back when the visual position changed but the calculated month didn't
          updates.start_month = newStartMonth;
        }
        
        if (durationChanged) updates.estimated_duration_months = newDuration;
        if (parentChanged) updates.parent_id = newParentId;
        if (newStrategyTitle) {
          updates.strategy_title = newStrategyTitle;
          updates.strategy_description = newStrategyDescription;
        }
        
        // If we're moving a program (not resizing), calculate the delta and update all children
        // For resize operations on programs, children should NOT move
        const hasChildren = isProgram;
        const monthDelta = startMonthChanged ? (newStartMonth - originalStartMonth) : 0;
        // Only move children if we're doing a MOVE operation, not a RESIZE
        const shouldMoveChildren = hasChildren && currentDragType === 'move' && monthDelta !== 0 && startMonthChanged;
        
        // ChatGPT Fix #5: Update localProjects FIRST, then clear drag state
        // IMMEDIATELY update local projects with the new position
        // This keeps things where the user dropped them and allows swimlane recalculation
        
        setLocalProjects(prevProjects => {
          // First, update the dragged project
          let updatedProjects = prevProjects.map(p => 
            p.id === project.id 
              ? { 
                  ...p, 
                  start_month: updates.start_month !== undefined ? updates.start_month : p.start_month,
                  estimated_duration_months: updates.estimated_duration_months !== undefined ? updates.estimated_duration_months : p.estimated_duration_months,
                  parent_id: updates.parent_id !== undefined ? (updates.parent_id || undefined) : p.parent_id,
                  strategy_title: updates.strategy_title !== undefined ? updates.strategy_title : (p as any).strategy_title,
                  strategy_description: updates.strategy_description !== undefined ? updates.strategy_description : (p as any).strategy_description
                }
              : p
          );
          
          // If this is a program and we're MOVING it (not resizing), update all descendants
          // For resize operations, children stay in place (only program's span changes)
          if (shouldMoveChildren) {
            const descendantIds = getAllDescendants(project.id, updatedProjects);
            updatedProjects = updatedProjects.map(p => {
              if (descendantIds.includes(p.id)) {
                return {
                  ...p,
                  start_month: Math.max(0, (p.start_month || 0) + monthDelta),
                  // If moving to a different strategy, update children's strategy info too
                  strategy_title: updates.strategy_title !== undefined ? updates.strategy_title : (p as any).strategy_title,
                  strategy_description: updates.strategy_description !== undefined ? updates.strategy_description : (p as any).strategy_description
                };
              }
              return p;
            });
          }
          
          // If moving to a different strategy (even without moving position), update all children's strategy info
          if (updates.strategy_title && !shouldMoveChildren) {
            const descendantIds = getAllDescendants(project.id, updatedProjects as RoadmapProject[]);
            updatedProjects = updatedProjects.map(p => {
              if (descendantIds.includes(p.id)) {
                return {
                  ...p,
                  strategy_title: updates.strategy_title,
                  strategy_description: updates.strategy_description
                } as typeof p;
              }
              return p;
            });
          }
          
          return updatedProjects;
        });
        
        // Mark this project and all its descendants as having a pending update
        // This prevents useEffect from resetting them while the backend update is in progress
        setPendingUpdates(prev => {
          const next = new Set(prev);
          next.add(project.id);
          // If we moved a program (not resized), also mark all children as pending
          if (shouldMoveChildren) {
            const descendantIds = getAllDescendants(project.id, localProjects);
            descendantIds.forEach(id => next.add(id));
          }
          return next;
        });
        
        // Track when this project was dragged (preserve position for 5 seconds even after pendingUpdates clears)
        lastDragTimeRef.current.set(project.id, Date.now());
        // Also track children if we moved a program
        if (shouldMoveChildren) {
          const descendantIds = getAllDescendants(project.id, localProjects);
          descendantIds.forEach(id => lastDragTimeRef.current.set(id, Date.now()));
        }
        
        // Save to backend in background
        // Once backend confirms, the parent will update the props
        // The useEffect will detect when props match local state and clear pendingUpdates automatically
        onTimelineUpdate(project.id, updates)
          .then(() => {
            // Backend update successful - DON'T clear pendingUpdates here
            // Let the useEffect handle it when it detects the props have been updated
            // The pendingUpdates will be cleared when the useEffect sees that the props
            // match the local state (meaning the backend update was successful and props are in sync)
          })
          .catch(err => {
            console.error('❌ Backend update failed:', err);
            // On error, revert localProjects and clear pending flag
            setPendingUpdates(prev => {
              const next = new Set(prev);
              next.delete(project.id);
              // Also clear children's pending flags if we moved a program
              if (shouldMoveChildren) {
                const descendantIds = getAllDescendants(project.id, localProjects);
                descendantIds.forEach(id => next.delete(id));
              }
              return next;
            });
            setLocalProjects(projects);
          });
        
              } catch (error) {
        console.error('❌ Failed to update project:', error);
        // On error, revert to original projects
        setLocalProjects(projects);
      }
    }
    // Note: Drag state is already cleared at the start of this function
  }, [onTimelineUpdate, localProjects, totalDuration]);

  // Add global mouse event listeners
  React.useEffect(() => {
    if (draggingProject) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        // Ensure drag state is cleared if component unmounts during drag
        if (draggingProjectRef.current) {
          draggingProjectRef.current = null;
          dragStartXRef.current = 0;
          dragTypeRef.current = null;
          setDraggingProject(null);
          setDragStartX(0);
          setDragCurrentX(0);
          setHasDragged(false);
          setDragType(null);
        }
      };
    }
  }, [draggingProject, handleMouseMove, handleMouseUp]);

  return (
    <div className="bg-[var(--color-surface-bg)] p-3 rounded-lg border border-[var(--color-border)]">
      {/* Timeline Scale - Gantt Style */}
      <div className="relative mb-4">
        <div className="flex border-b border-gray-300">
          {Array.from({ length: Math.ceil(totalDuration / 3) + 1 }, (_, i) => (
            <div key={i} className="flex-1 text-center text-xs text-gray-500 py-1 border-r border-gray-200">
              {i * 3}m
            </div>
          ))}
        </div>
      </div>

      {/* Strategies and Projects - Gantt Style with Swimlanes (One Chart Per Strategy) */}
      <div className="space-y-6">
        {Object.entries(strategies).map(([strategyName, strategyHierarchy], _strategyIndex) => {
          const strategyColor = generateInitiativeColor(_strategyIndex);
          // Flatten hierarchy for display
          const flattenedProjects = flattenHierarchy(strategyHierarchy);
          // Use calculated swimlanes that preserve assignments when possible
          const strategySwimlanes = swimlanes[strategyName] || [];
          
          // Get strategy description from first project
          const strategyDescription = flattenedProjects[0]?.strategy_description || '';
          
          // Separate programs and projects
          // Count programs (projects that have children - i.e., other projects have this as parent_id)
          // Programs are projects that contain other projects inside
          const programIds = new Set(flattenedProjects.map(p => p.parent_id).filter(Boolean));
          const programs = flattenedProjects.filter(p => programIds.has(p.id) || p.hierarchy_type === 'PROGRAM');
          
          // Count projects (leaf nodes - projects that don't have any children)
          // Projects don't contain any other projects inside
          const projects = flattenedProjects.filter(p => !programIds.has(p.id) && p.hierarchy_type !== 'PROGRAM');
          
          return (
            <div key={strategyName} data-strategy-name={strategyName} className="relative bg-[var(--color-surface-bg)] p-4 rounded-lg border border-[var(--color-border)]">
              {/* Strategy Header */}
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: strategyColor.primary }}
                  ></div>
                  <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
                    {strategyName}
                  </h3>
                </div>
                {strategyDescription && (
                  <p className="text-sm text-[var(--color-secondary-text)] ml-5 mb-2">
                    {strategyDescription}
                  </p>
                )}
                <div className="ml-5 text-xs text-[var(--color-secondary-text)]">
                  {programs.length > 0 && `${programs.length} program${programs.length !== 1 ? 's' : ''}`}
                  {programs.length > 0 && projects.length > 0 && ' and '}
                  {projects.length > 0 && `${projects.length} project${projects.length !== 1 ? 's' : ''}`}
                  {programs.length === 0 && projects.length === 0 && 'No projects'}
                </div>
              </div>

              {/* Gantt Timeline with Swimlanes */}
              <div ref={timelineRef} className="timeline-container relative border border-gray-200 rounded bg-white">
                {/* Timeline Grid */}
                <div className="flex border-b border-gray-300">
                  {Array.from({ length: Math.ceil(totalDuration / 3) + 1 }, (_, i) => (
                    <div key={i} className="flex-1 h-4 border-r border-gray-200"></div>
                  ))}
                </div>
                
                {/* Create a map of project ID to swimlane index for quick lookup */}
                {(() => {
                  const projectToSwimlaneMap = new Map<string, number>();
                  strategySwimlanes.forEach((swimlane, swimlaneIndex) => {
                    swimlane.forEach(project => {
                      projectToSwimlaneMap.set(project.id, swimlaneIndex);
                    });
                  });
                  
                  // Create arrays for each swimlane to hold projects in hierarchy order
                  const swimlaneProjects: RoadmapProject[][] = strategySwimlanes.map(() => []);
                  
                  // Iterate through flattened hierarchy (maintains order) and assign to swimlanes
                  flattenedProjects.forEach(project => {
                    const swimlaneIndex = projectToSwimlaneMap.get(project.id);
                    if (swimlaneIndex !== undefined) {
                      swimlaneProjects[swimlaneIndex].push(project);
                    }
                  });
                  
                  return strategySwimlanes.map((_, swimlaneIndex) => (
                    <div key={swimlaneIndex} className="relative border-b border-gray-100 last:border-b-0">
                      <div className="flex h-12">
                        {Array.from({ length: Math.ceil(totalDuration / 3) + 1 }, (_, i) => (
                          <div key={i} className="flex-1 border-r border-gray-100"></div>
                        ))}
                      </div>
                      
                      {/* Projects in this swimlane - rendered in hierarchy order */}
                      {swimlaneProjects[swimlaneIndex].map((project) => {
                        const projectNode = project as ProjectNode;
                        const level = projectNode.level || 0;
                        // const hasChildren = projectNode.children && projectNode.children.length > 0; // Unused
                        
                        const draftStatus = (project as { _draftStatus?: string })._draftStatus;
                        const effectiveColor = draftStatus && DRAFT_STATUS_COLORS[draftStatus]
                          ? DRAFT_STATUS_COLORS[draftStatus]
                          : strategyColor;
                        return (
                          <DraggableProject
                            key={project.id}
                            project={project}
                            initiativeColor={effectiveColor}
                            totalDuration={totalDuration}
                            isEditable={isEditable} // Allow dragging all projects, including parents
                            onProjectClick={onProjectClick}
                            onTimelineUpdate={onTimelineUpdate}
                            isDragging={draggingProject === project.id}
                            dragOffset={draggingProject === project.id ? dragCurrentX - dragStartX : 0}
                            hasDragged={draggingProject === project.id ? hasDragged : false}
                            dragType={draggingProject === project.id ? dragType : null}
                            dragStartX={draggingProject === project.id ? dragStartX : 0}
                            dragCurrentX={draggingProject === project.id ? dragCurrentX : 0}
                          onMouseDown={handleMouseDown}
                          hierarchyLevel={level}
                          isDropTarget={dropTarget === project.id}
                        />
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend - Compact */}
      <div className="mt-4 p-2 bg-gray-50 rounded border border-gray-200">
        <h4 className="font-medium text-[var(--color-surface-text)] mb-2 text-xs">{t('roadmapDetail.legend')}</h4>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-[var(--color-secondary-text)]">Active</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-[var(--color-secondary-text)]">Completed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-[var(--color-secondary-text)]">On Hold</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span className="text-[var(--color-secondary-text)]">Planned</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-gray-400 border-dashed"></div>
            <span className="text-[var(--color-secondary-text)]">Dependencies</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-yellow-600">★★★★☆</span>
            <span className="text-[var(--color-secondary-text)]">Complexity</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualRoadmapTimeline;
// Force reload to clear cache

